import React, { ReactNode } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl, { FormControlProps } from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent, SelectProps } from "@mui/material/Select";
import { Typography } from "@mui/material";

export interface BasicSelectAdditionalProps {
  handleChange: (value: string | string[]) => void;
  data: Array<{
    key: string;
    value: string;
    icon?: string;
    extra?: ReactNode | string;
  }>;
  value: string | string[] | undefined;
  id: string;
  label?: string;
  multiple?: boolean;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  isLoading?: boolean;
  displayEmpty?: boolean;
  size?: FormControlProps["size"];
  maxWidth?: string | number;
  showClearOption?: boolean;
  width?: string | number;
  backgroundColor?: string | number;
}

export type BasicSelectProps = BasicSelectAdditionalProps & SelectProps;

const SelectComponent: React.FC<BasicSelectProps> = ({
  handleChange,
  data,
  value,
  id,
  label,
  multiple = false,
  required = false,
  error = false,
  errorMessage,
  isLoading = false,
  displayEmpty = false,
  size = "medium",
  maxWidth = "100%",
  showClearOption = false,
  width = "100%",
  backgroundColor,
  ...otherProps
}) => {
  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string | string[];
    handleChange(
      multiple && Array.isArray(selectedValue)
        ? selectedValue
        : (selectedValue as string),
    );
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleChange(multiple ? [] : "");
    setTimeout(() => {
      const element = document.activeElement as HTMLElement;
      if (element) {
        element.blur();
      }
    }, 0);
  };

  return (
    <Box
      sx={{
        minWidth: 180,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        maxWidth: {
          xs: "100%",
          sm: "50%",
          md: maxWidth,
        },
        width,
        ...(backgroundColor ? { backgroundColor } : {}),
      }}
    >
      <FormControl
        sx={{ height: size === "medium" ? "3.5rem" : "auto" }}
        size={size}
        error={error}
        required={required}
        fullWidth
      >
        {!displayEmpty && <InputLabel id={`${id}-label`}>{label}</InputLabel>}
        <Select
          {...otherProps}
          labelId={`${id}-label`}
          id={id}
          value={value || ""}
          label={label}
          onChange={handleSelectChange}
          multiple={multiple}
          displayEmpty={displayEmpty}
          disabled={isLoading || otherProps.disabled}
          renderValue={
            multiple
              ? (selected: any) => {
                  const values = selected
                    .map(
                      (key: string) =>
                        data.find((item) => item.key === key)?.value,
                    )
                    .filter(Boolean);
                  return values.join(", ");
                }
              : undefined
          }
        >
          {showClearOption && (
            <MenuItem value="" onClick={handleClear}>
              <Typography sx={{ fontStyle: "italic" }}>(Clear)</Typography>
            </MenuItem>
          )}
          {data.map((item) => (
            <MenuItem key={item.key} value={item.key}>
              {multiple ? (
                <>
                  <Checkbox
                    checked={Array.isArray(value) && value.includes(item.key)}
                  />
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "11.25rem", // 180px
                      fontSize: "inherit",
                    }}
                  >
                    {item.value}
                  </Typography>
                </>
              ) : item.icon ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.value}
                    sx={{ height: "1.5rem", width: "1.5rem" }} // 24px
                  />
                  <ListItemText>
                    <Typography
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "11.25rem", // 180px
                      }}
                    >
                      {item.value}
                    </Typography>
                  </ListItemText>
                </Box>
              ) : item.extra ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {item.extra}
                  <ListItemText>
                    <Typography
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "11.25rem", // 180px
                      }}
                    >
                      {item.value}
                    </Typography>
                  </ListItemText>
                </Box>
              ) : (
                item.value
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {errorMessage && (
        <FormHelperText className="px-3.5 pt-0.5 !text-error-main">
          {errorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};

SelectComponent.displayName = "Select";

export default SelectComponent;
